import { memo } from 'react';
import type { OrderWithItems } from '@/store/seller';
import type { OrderType } from '@/types';
import { PickupTimeDisplay } from '@/components/order/PickupTimeDisplay';
import { TimeoutTimer } from './TimeoutTimer';

interface OrderCardProps {
  order: OrderWithItems;
  onActionClick: (orderId: string, action: string) => void | Promise<void>;
  onDetailClick?: (order: OrderWithItems) => void;
  variant: 'pending' | 'processing' | 'completed' | 'cancelled';
}

const orderTypeLabels: Record<OrderType, string> = {
  DEADLINE_DISCOUNT: '마감할인',
  RESERVATION_DISCOUNT: '예약할인', 
  REGULAR_ORDER: '일반주문'
};

const orderTypeBadgeColors: Record<OrderType, string> = {
  DEADLINE_DISCOUNT: 'bg-orange-100 text-orange-600',
  RESERVATION_DISCOUNT: 'bg-blue-100 text-blue-600',
  REGULAR_ORDER: 'bg-gray-100 text-gray-600'
};

export const OrderCard = memo(function OrderCard({ 
  order, 
  onActionClick,
  onDetailClick,
  variant 
}: OrderCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  // 상태별 뱃지 표시 (READY 탭에서 복합 상태 표시용)
  const getStatusBadge = () => {
    switch (order.status) {
      case 'READY':
        return { 
          text: '픽업 대기중', 
          bgColor: 'bg-blue-100', 
          textColor: 'text-blue-700',
          icon: '⏰'
        };
      case 'COMPLETED':
        return { 
          text: '픽업 완료', 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-700',
          icon: '✅'
        };
      case 'CANCELLED':
        // cancellation_reason에 따른 세분화
        let text = '취소됨';
        if (order.cancellation_reason?.includes('수락기한') || order.cancellation_reason?.includes('자동')) {
          text = '자동취소';
        } else if (order.cancellation_reason?.includes('고객')) {
          text = '고객취소';
        } else if (order.cancellation_reason?.includes('판매자') || order.cancellation_reason?.includes('거절')) {
          text = '판매자거절';
        } else if (order.cancellation_reason?.includes('환불') || order.cancellation_reason?.includes('제품')) {
          text = '환불됨';
        }
        return { 
          text, 
          bgColor: 'bg-red-100', 
          textColor: 'text-red-700',
          icon: '❌'
        };
      case 'NOSHOW':
        return { 
          text: '노쇼', 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-700',
          icon: '⚠️'
        };
      default:
        return null;
    }
  };

  const getItemsText = () => {
    if (!order.items || order.items.length === 0) {
      return '상품 정보 없음';
    }
    
    const firstItem = order.items[0];
    const restCount = order.items.length - 1;
    
    if (restCount > 0) {
      return `${firstItem.product_name} 외 ${restCount}개`;
    }
    
    return firstItem.product_name;
  };

  const getActionButton = () => {
    const baseClasses = "w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-colors";
    
    switch (variant) {
      case 'pending':
        return (
          <div className="flex gap-2">
            <button
              onClick={() => onActionClick(order.id, 'accept')}
              className={`${baseClasses} flex-1 bg-green-500 text-white hover:bg-green-600`}
            >
              접수하기
            </button>
            <button
              onClick={() => onActionClick(order.id, 'reject')}
              className={`${baseClasses} flex-1 bg-orange-500 text-white hover:bg-orange-600`}
            >
              주문거절
            </button>
          </div>
        );
      case 'processing':
        return (
          <div className="flex gap-2">
            <button
              onClick={() => onActionClick(order.id, 'ready')}
              className={`${baseClasses} flex-1 bg-blue-500 text-white hover:bg-blue-600`}
            >
              준비완료
            </button>
            <button
              onClick={() => onActionClick(order.id, 'refund')}
              className={`${baseClasses} flex-1 bg-orange-500 text-white hover:bg-orange-600`}
            >
              환불하기
            </button>
          </div>
        );
      case 'completed':
        // READY 상태일 때만 두 버튼 표시
        if (order.status === 'READY') {
          return (
            <div className="flex gap-2">
              <button
                onClick={() => onActionClick(order.id, 'complete')}
                className={`${baseClasses} flex-1 bg-blue-500 text-white hover:bg-blue-600`}
              >
                수령완료
              </button>
              <button
                onClick={() => onActionClick(order.id, 'refund')}
                className={`${baseClasses} flex-1 bg-orange-500 text-white hover:bg-orange-600`}
              >
                환불하기
              </button>
            </div>
          );
        }
        // 나머지 상태는 기존 환불하기 버튼만
        return (
          <button
            onClick={() => onActionClick(order.id, 'refund')}
            className={`${baseClasses} bg-orange-500 text-white hover:bg-orange-600`}
          >
            환불하기
          </button>
        );
      case 'cancelled':
        return (
          <button
            disabled
            className={`${baseClasses} bg-gray-300 text-gray-500 cursor-not-allowed`}
          >
            취소주문
          </button>
        );
      default:
        return null;
    }
  };

  const cardBorderClass = variant === 'cancelled' 
    ? 'border-2 border-red-300' 
    : 'border border-gray-200';

  return (
    <div className={`bg-white rounded-lg p-4 mb-3 ${cardBorderClass}`}>
      {/* 주문 유형 배지 및 타이머 */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${orderTypeBadgeColors[order.order_type]}`}>
            {orderTypeLabels[order.order_type]}
          </span>
          {/* READY 탭에서 복합 상태 표시 */}
          {variant === 'completed' && getStatusBadge() && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge()!.bgColor} ${getStatusBadge()!.textColor}`}>
              {getStatusBadge()!.icon} {getStatusBadge()!.text}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* 상세 버튼 - 모든 상태에서 표시 */}
          <button
            onClick={() => {
              if (onDetailClick) {
                onDetailClick(order);
              } else {
                console.error('상세 버튼 핸들러가 전달되지 않았습니다');
              }
            }}
            className="px-3 py-1 bg-blue-50 text-blue-600 text-sm font-medium rounded-md hover:bg-blue-100 transition-colors border border-blue-200"
          >
            상세
          </button>
          {/* 타임아웃 타이머 표시 (PENDING 또는 ACCEPTED 상태일 때) */}
          {(variant === 'pending' || variant === 'processing') && (
            <TimeoutTimer order={order} />
          )}
          {variant === 'cancelled' && (
            <span className="text-red-500 text-xs font-medium">취소주문</span>
          )}
        </div>
      </div>

      {/* 주문 정보 */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">주문자</span>
          <span className="font-medium">{order.buyer_nickname}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">금액</span>
          <span className="font-bold text-green-600">{formatCurrency(order.total_amount)}원</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">주문번호</span>
          <span className="font-medium text-gray-800">{order.order_number}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">주문품목</span>
          <span className="font-medium text-right text-sm max-w-[180px] truncate">
            {getItemsText()}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">수령시간</span>
          <div className="text-right">
            <PickupTimeDisplay 
              order={{
                ...order,
                items: order.items?.map(item => ({
                  ...item,
                  product: item.products ? { id: '', name: item.products.name, original_price: 0 } : undefined
                }))
              } as any}
              className="text-blue-600 text-sm justify-end"
            />
          </div>
        </div>
      </div>

      {/* 액션 버튼 */}
      {getActionButton()}
    </div>
  );
});
